// @ts-nocheck
import { useState, useEffect, useCallback, useRef } from "react"
import { useInView } from "react-intersection-observer"
import { motion, AnimatePresence } from "framer-motion"
import { supabaseClient } from "@/lib/supabase/client"
import { PostCard } from "@/components/post/PostCard"
import { cn } from "@/lib/utils/cn"

interface Props {
  userId: string | null
  subscribedTopics: string[]
  topicId?: string
  initialSort?: string
}

const PAGE_SIZE = 20

export function FeedContainer({ userId, subscribedTopics, topicId, initialSort = "new" }: Props) {
  const [posts, setPosts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [sort, setSort] = useState(initialSort)
  const [page, setPage] = useState(0)
  const { ref: bottomRef, inView } = useInView({ threshold: 0, rootMargin: "300px" })

  const fetchPosts = useCallback(async (reset = false, currentSort = sort, currentPage = 0) => {
    reset ? setLoading(true) : setLoadingMore(true)
    try {
      let q = supabaseClient
        .from("posts")
        .select(`
          id, title, content, slug, vote_score, comment_count, published_at, created_at,
          status, media_urls, post_type,
          users!posts_author_id_fkey(id, username, display_name, avatar_url, is_verified),
          post_topics(topics(id, name, slug, color))
        `)
        .eq("status", "published")
        .range(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE - 1)

      if (topicId) q = q.eq("post_topics.topic_id", topicId)

      if (currentSort === "new") q = q.order("created_at", { ascending: false })
      else if (currentSort === "top") q = q.order("vote_score", { ascending: false })
      else q = q.order("created_at", { ascending: false }) // hot fallback

      const { data, error } = await q
      if (error) throw error

      const shaped = (data || []).map((p: any) => ({
        ...p,
        author: p.users,
        topic: p.post_topics?.[0]?.topics || null,
        user_vote: null,
        user_bookmarked: false,
        vote_score: p.vote_score || 0,
        comment_count: p.comment_count || 0,
      }))

      if (reset) setPosts(shaped)
      else setPosts(prev => [...prev, ...shaped])
      setHasMore((data || []).length === PAGE_SIZE)
      if (!reset) setPage(currentPage)
    } catch (e) {
      console.error("Feed fetch error:", e)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [sort, topicId])

  useEffect(() => { fetchPosts(true, sort, 0); setPage(0) }, [sort, topicId])
  useEffect(() => { if (inView && hasMore && !loadingMore && !loading) fetchPosts(false, sort, page + 1) }, [inView])

  const SORTS = [
    { key: "new", label: "New" },
    { key: "top", label: "Top" },
    { key: "hot", label: "Hot" },
  ]

  return (
    <div>
      {/* Sort bar */}
      <div className="panel mb-2 p-2 flex items-center gap-1">
        {SORTS.map(s => (
          <button
            key={s.key}
            onClick={() => setSort(s.key)}
            className={`btn-ghost text-xs font-bold uppercase tracking-wider px-3 py-1.5 ${
              sort === s.key ? "bg-muted text-foreground" : ""
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? <FeedSkeleton /> : posts.length === 0 ? (
        <div className="panel p-12 text-center">
          <p className="font-syne font-bold text-lg mb-1">Nothing here yet</p>
          <p className="text-sm text-muted-fg">Be the first to post something.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {posts.map((post, i) => (
            <motion.div key={post.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: Math.min(i * 0.03, 0.2) }}>
              <PostCard post={post} userId={userId} />
            </motion.div>
          ))}
          <div ref={bottomRef} className="h-4" />
          {loadingMore && <FeedSkeleton count={3} />}
          {!hasMore && posts.length > 0 && (
            <div className="panel p-6 text-center text-sm text-muted-fg font-semibold">
              You've seen everything — you're all caught up.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function FeedSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="post-card flex">
          <div className="w-10 bg-[hsl(0,0%,8%)] flex-shrink-0" />
          <div className="p-3 flex-1 space-y-2">
            <div className="skeleton h-3 w-48 rounded" />
            <div className="skeleton h-5 w-3/4 rounded" />
            <div className="skeleton h-3.5 w-full rounded" />
            <div className="skeleton h-3.5 w-2/3 rounded" />
            <div className="flex gap-2 mt-2">
              <div className="skeleton h-6 w-16 rounded" />
              <div className="skeleton h-6 w-20 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}